import type {
  AskAiStartersResponse,
  BrandAlignmentApiResult,
  DashboardResponse,
  DateRangeParams,
  InsightsApiResult,
  ProductsResponse,
  TokenUsageResponse,
  TrendsResponse,
} from '@/types/analytics'
import { BaseService } from '../base'

/** UUID v4 format validation regex */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Validates that a string is a valid UUID format.
 * @param id - The string to validate
 * @param name - The name of the parameter for error messages
 * @throws Error if the string is not a valid UUID
 */
function validateUUID(id: string, name: string): void {
  if (!UUID_REGEX.test(id)) {
    throw new Error(`Invalid ${name}: must be a valid UUID`)
  }
}

/**
 * Service class for fetching analytics data for chat configurations.
 * All endpoints are scoped to a specific chat configuration ID.
 *
 * Note: Query params use snake_case (olive_branch only converts JSON bodies)
 */
export class AnalyticsService extends BaseService {
  protected readonly baseURL = '/analytics'

  /**
   * Get dashboard metrics for a chat configuration.
   * Returns totals, time series data, and browser breakdown.
   * @throws Error if chatConfigurationId is not a valid UUID
   */
  async getDashboard(
    chatConfigurationId: string,
    params: DateRangeParams
  ): Promise<DashboardResponse> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    return this.get<DashboardResponse>(`/${chatConfigurationId}/dashboard`, {
      start_date: params.startDate,
      end_date: params.endDate,
    })
  }

  /**
   * Get trend metrics for a chat configuration.
   * Returns daily conversation and session trends with summaries.
   * @throws Error if chatConfigurationId is not a valid UUID
   */
  async getTrends(
    chatConfigurationId: string,
    params: DateRangeParams
  ): Promise<TrendsResponse> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    return this.get<TrendsResponse>(`/${chatConfigurationId}/trends`, {
      start_date: params.startDate,
      end_date: params.endDate,
    })
  }

  /**
   * Get product performance metrics for a chat configuration.
   * Returns top products by views and clicks with CTR.
   * @throws Error if chatConfigurationId is not a valid UUID
   */
  async getProducts(
    chatConfigurationId: string,
    params: DateRangeParams
  ): Promise<ProductsResponse> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    return this.get<ProductsResponse>(`/${chatConfigurationId}/products`, {
      start_date: params.startDate,
      end_date: params.endDate,
    })
  }

  /**
   * Get conversation insights (theme analysis) for a chat configuration.
   * The API responds synchronously with aggregator output.
   */
  async getInsights(
    chatConfigurationId: string,
    params: DateRangeParams & { refresh?: boolean }
  ): Promise<InsightsApiResult> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    const queryParams: Record<string, string> = {
      start_date: params.startDate,
      end_date: params.endDate,
    }

    const response = await this.apiClient.get(
      `${this.baseURL}/${chatConfigurationId}/insights`,
      { params: queryParams }
    )

    return { data: response.data.data }
  }

  /**
   * Get brand alignment and trust analysis for a chat configuration.
   * Returns processing status (202) or ready data (200).
   */
  async getBrandAlignment(
    chatConfigurationId: string,
    params: DateRangeParams & { refresh?: boolean }
  ): Promise<BrandAlignmentApiResult> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    const queryParams: Record<string, string> = {
      start_date: params.startDate,
      end_date: params.endDate,
    }
    if (params.refresh) {
      queryParams.refresh = 'true'
    }

    const response = await this.apiClient.get(
      `${this.baseURL}/${chatConfigurationId}/brand_alignment`,
      { params: queryParams }
    )

    if (response.status === 202) {
      return { status: 'processing' }
    }

    return { status: 'ready', data: response.data.data }
  }

  /**
   * Export product performance data as CSV.
   * Returns a blob and suggested filename for download.
   * @throws Error if chatConfigurationId is not a valid UUID
   */
  async exportProductsCsv(
    chatConfigurationId: string,
    params: DateRangeParams
  ): Promise<{ blob: Blob; filename: string }> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    const response = await this.apiClient.get(
      `${this.baseURL}/${chatConfigurationId}/export_products`,
      {
        params: {
          start_date: params.startDate,
          end_date: params.endDate,
        },
        responseType: 'blob',
      }
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
    const filename =
      filenameMatch?.[1] ||
      `product-analytics-${params.startDate}-to-${params.endDate}.csv`

    return { blob: response.data, filename }
  }

  /**
   * Get LLM-generated Ask AI cold-start suggestions for the analytics context.
   * Returns 4 questions tailored to the config's recent conversation data,
   * or an empty suggestions array with generated_from: "fallback" if no data.
   * @throws Error if chatConfigurationId is not a valid UUID
   */
  async getAskAiStarters(
    chatConfigurationId: string
  ): Promise<AskAiStartersResponse> {
    validateUUID(chatConfigurationId, 'chatConfigurationId')

    return this.get<AskAiStartersResponse>(
      `/${chatConfigurationId}/ask_ai_starters`
    )
  }

  /**
   * Get token usage totals + time series for the caller's organization.
   * Hits a different base path: /organizations/token_usage.
   */
  async getOrganizationTokenUsage(
    params: DateRangeParams & { granularity?: 'daily' | 'weekly' | 'monthly' }
  ): Promise<TokenUsageResponse> {
    const query: Record<string, string> = {
      start_date: params.startDate,
      end_date: params.endDate,
    }

    if (params.granularity) query.granularity = params.granularity

    // /organizations/token_usage is outside this service's baseURL — call apiClient directly.
    const response = await this.apiClient.get('/organizations/token_usage', {
      params: query,
    })

    return response.data.data
  }
}

export const analyticsService = new AnalyticsService()
